import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isTokenValid as validateToken, Session } from '@feathr/blackbox';
import type { ICaseStudy } from '@feathr/components';
import { AlertV2 as Alert, Button, EAlertV2Type, showError, toast } from '@feathr/components';
import { getQuery } from '@feathr/hooks';

import type { IPasswords } from '../../components';
import {
  CompleteProfile,
  LoginForm,
  PageV2,
  PasswordInputs,
  resetPassword,
} from '../../components';
import { redirect } from '../App';

import * as styles from './InvitePage.css';

interface IProps {
  caseStudy: ICaseStudy;
}

function InvitePage({ caseStudy }: Readonly<IProps>): JSX.Element {
  const { t: token } = getQuery<{ t: string }>();
  const isTokenValid = validateToken(token);

  const [{ password1, password2, isValid: passwordsAreValid }, setPasswords] = useState<IPasswords>(
    {
      password1: '',
      password2: '',
      isValid: false,
    },
  );
  const [currentStep, setCurrentStep] = useState<'password' | 'profile'>('password');

  const { t } = useTranslation();

  async function handleSubmit(): Promise<void> {
    try {
      await resetPassword({
        t,
        token,
        password1,
        password2,
        successMessage: t('Password created successfully.'),
        successRedirectPath: '',
      });

      // Move to profile completion step
      toast(t('Password created successfully.'), { type: 'success' });
      setCurrentStep('profile');
    } catch (error) {
      showError(error, t);
    }
  }

  function handleProfileComplete(): void {
    // Redirect to extender app - let it determine onboarding vs dashboard based on account type
    toast(t('Your account has been created successfully.'), { type: 'success' });
    redirect();
  }

  const userId = Session.userId;

  function getDescription(): string | undefined {
    if (!isTokenValid) {
      return undefined;
    }
    if (currentStep === 'password') {
      return t('Create a secure password to finish setting up your account.');
    }
    return t('Add your details to finish setting up your account.');
  }

  function renderContent(): JSX.Element {
    if (!isTokenValid) {
      return (
        <Alert
          title={t('Your invitation link has expired or has already been used.')}
          type={EAlertV2Type.danger}
        >
          <Button className={styles.link} link={'/'} type={'link'}>
            {t('Return to the log in page')}
          </Button>
        </Alert>
      );
    }

    if (currentStep === 'password') {
      return <PasswordInputs onChange={setPasswords} t={t} />;
    }

    if (userId) {
      return <CompleteProfile onComplete={handleProfileComplete} userId={userId} />;
    }

    return <Alert title={t('Unable to load user information.')} type={EAlertV2Type.danger} />;
  }

  return (
    <PageV2 caseStudy={caseStudy} showBackButton={!isTokenValid}>
      <LoginForm
        actions={
          isTokenValid && currentStep === 'password' ? (
            <Button disabled={!passwordsAreValid} onClick={handleSubmit} type={'primary'}>
              {t('Create account')}
            </Button>
          ) : undefined
        }
        description={getDescription()}
        title={t('Complete your profile')}
      >
        {renderContent()}
      </LoginForm>
    </PageV2>
  );
}

export default InvitePage;
